<template>
  <v-expansion-panels
    multiple
    :value="openCoreStepPanels"
    id="getting-started-guide-panels"
    accordion
    v-if="shouldRenderGuide">
    <v-expansion-panel>
      <v-expansion-panel-header>
        <v-progress-circular
          :value="corePercentCompleted"
          color="primary"
          size="25"
          rotate="-90"
          class="flex-grow-0 mr-4"></v-progress-circular>
        <h3>Get Started</h3>
      </v-expansion-panel-header>
      <v-expansion-panel-content>
        <!-- Core setup steps -->
        <template v-if="!areCoreStepsCompleted">
          <div>
            <v-progress-linear
              :value="corePercentCompleted"
              height="6"
              background-color="disabled"
              color="primary">
              <div class="full-width full-height is-relative">
                <span
                  v-for="i in totalCoreSteps"
                  :class="{ completed: currentCoreStep > i }"
                  class="dot d-block"
                  :key="`step-${i}`"
                  :style="`left: calc(${(i / totalCoreSteps) * 100}% - 6px);`"></span>
              </div>
            </v-progress-linear>
            <div class="d-flex justify-space-between mt-2 font-size-x-small">
              <span>IN PROGRESS</span>
              <span>{{ coreStepsCompleted }}/{{ totalCoreSteps }}</span>
            </div>
          </div>
          <v-stepper v-model="currentCoreStep" vertical color="primary" flat>
            <v-stepper-step :complete="isStep1Complete" step="1">
              <v-icon v-if="isStep1Complete">mdi-check-circle</v-icon>
              <v-icon v-else>mdi-circle-outline</v-icon>
              Create your Organization
            </v-stepper-step>

            <v-stepper-content step="1">
              <p class="font-size-small">
                To start using Opendock, you need to name your Organization.
              </p>
              <div class="mb-4">
                <create-org-dialog redirect-path="/warehouses"></create-org-dialog>
              </div>
            </v-stepper-content>

            <v-stepper-step :complete="isStep2Complete" step="2">
              <v-icon v-if="isStep2Complete">mdi-check-circle</v-icon>
              <v-icon v-else>mdi-circle-outline</v-icon>
              <template>Create a Warehouse</template>
            </v-stepper-step>

            <v-stepper-content step="2">
              <p class="font-size-x-small">Now create a warehouse.</p>
              <div class="mb-4">
                <create-warehouse-dialog
                  v-if="currentCoreStep === 2"
                  @close="shouldShowWarehouseDialog = false"
                  :show-dialog="shouldShowWarehouseDialog"
                  :is-first-warehouse="true"
                  before-icon=""
                  class="mb-4"
                  post-submission-route-name="warehouses.details.operatingHours"></create-warehouse-dialog>
              </div>
            </v-stepper-content>

            <v-stepper-step :complete="isStep3Complete" step="3">
              <v-icon v-if="isStep3Complete">mdi-check-circle</v-icon>
              <v-icon v-else>mdi-circle-outline</v-icon>
              <template>Add Operating Hours</template>
            </v-stepper-step>

            <v-stepper-content step="3">
              <p class="font-size-x-small">
                Now that we have a warehouse, we need to add its operating hours.
              </p>
              <div class="mb-4">
                <create-warehouse-schedule-dialog
                  v-if="$appWarehouse"
                  :warehouse="$appWarehouse"
                  postSubmissionRouteName="warehouses.details.loadTypes"></create-warehouse-schedule-dialog>
              </div>
            </v-stepper-content>

            <v-stepper-step :complete="isStep4Complete" step="4">
              <v-icon v-if="isStep4Complete">mdi-check-circle</v-icon>
              <v-icon v-else>mdi-circle-outline</v-icon>
              Create a Warehouse Load Type
            </v-stepper-step>

            <v-stepper-content step="4">
              <p class="font-size-x-small">
                Great! Now it's time to create the load types your warehouses uses to operate.
              </p>
              <div class="mb-4">
                <create-load-type-dialog
                  before-icon=""
                  :warehouse="$appWarehouse"
                  postSubmissionRouteName="warehouses.details.docks">
                </create-load-type-dialog>
              </div>
            </v-stepper-content>

            <v-stepper-step :complete="isStep5Complete" step="5">
              <v-icon v-if="isStep5Complete">mdi-check-circle</v-icon>
              <v-icon v-else>mdi-circle-outline</v-icon>
              <template>Create a Dock</template>
            </v-stepper-step>

            <v-stepper-content step="5">
              <p class="font-size-x-small">Create a Dock for your Warehouse</p>
              <div class="mb-4">
                <create-dock-dialog
                  v-if="$appWarehouse"
                  :warehouse-id="$appWarehouse.id"
                  before-icon=""></create-dock-dialog>
              </div>
            </v-stepper-content>
          </v-stepper>
        </template>

        <!-- Optional setup steps -->
        <template v-else>
          <div class="text-center">
            <img :src="images.checkMark" />
            <h2>You're all done here!</h2>
            <p class="font-size-small pt-1 font-weight-bold">What would you like to do next?</p>
          </div>
          <v-expansion-panels
            id="internal-getting-started-panels"
            v-model="openAdditionalStepPanels"
            accordion
            flat>
            <v-expansion-panel :disabled="$hasAppointment">
              <v-expansion-panel-header class="px-0 justify-start">
                <v-icon v-if="!$hasAppointment" class="flex-grow-0 mr-3">mdi-circle-outline</v-icon>
                <v-icon v-else class="flex-grow-0 mr-3" color="primary">mdi-check-circle</v-icon>
                <span class="font-weight-regular">Add an Appointment</span>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <p class="font-size-small">
                  With your warehouse all set up, now you can add appointments to your schedule.
                </p>
                <create-appointment-dialog></create-appointment-dialog>
              </v-expansion-panel-content>
            </v-expansion-panel>
            <v-expansion-panel :disabled="$hasInvitedUser">
              <v-expansion-panel-header class="px-0 justify-start">
                <v-icon v-if="!$hasInvitedUser" class="flex-grow-0 mr-3">mdi-circle-outline</v-icon>
                <v-icon v-else class="flex-grow-0 mr-3" color="primary">mdi-check-circle</v-icon>
                <span class="font-weight-regular">Invite users to your Organization</span>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <p class="font-size-small">Assign roles and share your tasks with more users.</p>
                <invite-user-dialog
                  post-submission-route-name="users"
                  before-icon=""
                  button-label="Invite New User"></invite-user-dialog>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </template>

        <v-btn v-if="$org && $org.id" text block class="mt-2" @click="dismissBox">Dismiss</v-btn>
      </v-expansion-panel-content>
    </v-expansion-panel>
  </v-expansion-panels>
</template>

<script>
import CheckMarkImg from '@/../public/images/check_mark.svg';
import InviteUserDialog from '../users/components/InviteUserDialog';

export default {
  components: { InviteUserDialog },
  props: {
    waitTCConfirmation: {
      type: Boolean,
      required: true
    }
  },
  computed: {
    queryParams() {
      return {
        params: {
          limit: 1,
          s: {
            orgId: {
              $eq: this.$me.orgId
            }
          }
        }
      };
    },
    isStep1Complete() {
      return this.currentCoreStep > 1 || this.hasOrg;
    },
    isStep2Complete() {
      return this.currentCoreStep > 2 || Boolean(this.$appWarehouse?.id);
    },
    isStep3Complete() {
      return this.currentCoreStep > 3 || this.$hasSchedule;
    },
    isStep4Complete() {
      return this.currentCoreStep > 4 || this.$hasLoadType;
    },
    isStep5Complete() {
      return this.currentCoreStep > 5 || this.$hasDock;
    },
    areCoreStepsCompleted() {
      return this.corePercentCompleted === 100;
    },
    areAdditionalStepsCompleted() {
      return this.$hasAppointment && this.$hasInvitedUser;
    },
    coreStepsCompleted() {
      let stepsCompleted = 0;
      for (let i = 1; i <= this.totalCoreSteps; i++) {
        if (this[`isStep${i}Complete`]) {
          stepsCompleted++;
        }
      }
      return stepsCompleted;
    },
    corePercentCompleted() {
      return (this.coreStepsCompleted / this.totalCoreSteps) * 100;
    },
    hasOrg() {
      return Boolean(this.$me?.orgId);
    },
    shouldUserSeeGuide() {
      return (
        this.$allowedUserRoles.includes(this.$me?.role) ||
        (!this.hasOrg && this.$me?.role === this.novaCore.UserRole.BASE)
      );
    },
    isBoxReady() {
      return this.mounted && this.$me?.id && this.$route.meta.requiresAuth;
    },
    shouldRenderGuide() {
      return (
        !this.waitTCConfirmation &&
        this.$route.name !== 'appointmentsTV' &&
        this.shouldUserSeeGuide &&
        this.isBoxReady &&
        !this.$isOnboarded
      );
    },
    shouldBeOnboarded() {
      return !this.$nonOnboardableRoles.includes(this.$me?.role);
    }
  },
  data() {
    return {
      mounted: false,
      currentCoreStep: 1,
      totalCoreSteps: 5,
      images: {
        checkMark: CheckMarkImg
      },
      openCoreStepPanels: [0],
      openAdditionalStepPanels: [],
      shouldShowWarehouseDialog: false
    };
  },
  methods: {
    doesEventOrgMatchUserOrg(orgId) {
      return orgId === this.$me.orgId;
    },
    async getOneEntity(entityName, storeMutation, params) {
      // TODO: Searching by Org ID here temporarily - need to remove after NOVA-840 is completed
      let response = await axios.get(entityName.toLowerCase(), params || this.queryParams);
      if (response?.data) {
        this.$store.commit(`App/${storeMutation}`, response.data.data.length > 0);
      }
    },
    async getOneLoadType() {
      if (this.$appWarehouse?.id) {
        const response = await axios.get('loadtype', {
          params: { warehouseId: this.$appWarehouse.id }
        });
        if (response?.data) {
          this.$store.commit(`App/setHasLoadType`, response.data.data.length > 0);
        }
      }
    },
    async getOneAppointment() {
      const response = await axios.get('appointment', this.queryParams);
      if (response?.data) {
        this.$store.commit(
          `App/setHasAppointment`,
          response.data.data.filter(appt => !this.novaCore.isReserve(appt)).length > 0
        );
      }
    },
    async getOneOrg() {
      if (this.$me.orgId) {
        const response = await axios.get(`org/${this.$me.orgId}`);
        if (response?.data) {
          this.$store.commit(`App/setOrg`, response.data.data);
        }
      }
    },
    async getOneWarehouse() {
      // TODO: Searching by Org ID here temporarily - need to remove after NOVA-840 is completed
      const warehouses = await this.services.warehouse.getWarehouses(this.queryParams, {
        fields: ['name', 'schedule']
      });
      if (warehouses?.length > 0) {
        this.$store.commit('App/setWarehouse', warehouses[0]);
        this.$store.commit('App/setHasSchedule', Boolean(warehouses[0]?.schedule));
      }
    },
    async getOneOrgUser() {
      // TODO: Searching by Org ID here temporarily - need to remove after NOVA-840 is completed
      let response = await axios.get('user', {
        params: {
          limit: 1,
          s: {
            $and: [
              {
                id: {
                  $ne: this.$me.id
                }
              },
              {
                orgId: {
                  $eq: this.$me.orgId,
                  $notnull: true
                }
              }
            ]
          }
        }
      });
      if (response?.data) {
        this.$store.commit('App/setHasInvitedUser', response.data.data.length > 0);
      }
    },
    onLoadTypeCreated(payload) {
      if (this.doesEventOrgMatchUserOrg(payload.orgId)) {
        this.$store.commit('App/setHasLoadType', true);
      }
    },
    onWarehouseCreated(payload) {
      if (this.doesEventOrgMatchUserOrg(payload.orgId)) {
        this.$store.commit('App/setWarehouse', payload);
      }
    },
    onWarehouseUpdated(payload) {
      if (this.doesEventOrgMatchUserOrg(payload.orgId)) {
        if (payload.schedule) {
          this.$store.commit('App/setHasSchedule', true);
        }
      }
    },
    onDockCreated(payload) {
      if (this.doesEventOrgMatchUserOrg(payload.orgId)) {
        this.$store.commit('App/setHasDock', true);

        if (this.$appWarehouse?.id) {
          this.navigate('warehouses.details.docks', { warehouseId: this.$appWarehouse.id });
          this.$eventHub.$emit(`update-Warehouse-${this.$appWarehouse.id}`);
        }
      }
    },
    onAppointmentCreated(payload) {
      if (this.doesEventOrgMatchUserOrg(payload.orgId)) {
        if (!this.novaCore.isReserve(payload)) {
          this.$store.commit('App/setHasAppointment', true);
        }
      }
    },
    onUserCreated(payload) {
      if (payload.id !== this.$me.id) {
        this.$store.commit('App/setHasInvitedUser', true);
      }
    },
    registerListeners() {
      this.$eventHub.$on('create-LoadType', this.onLoadTypeCreated);
      this.$eventHub.$on('create-Warehouse', this.onWarehouseCreated);
      this.$eventHub.$on('update-Warehouse', this.onWarehouseUpdated);
      this.$eventHub.$on('create-Dock', this.onDockCreated);
      this.$eventHub.$on('create-Appointment', this.onAppointmentCreated);
      this.$eventHub.$on('create-User', this.onUserCreated);
    },
    destroyListeners() {
      this.$eventHub.$off('create-LoadType', this.onLoadTypeCreated);
      this.$eventHub.$off('create-Warehouse', this.onWarehouseCreated);
      this.$eventHub.$off('update-Warehouse', this.onWarehouseUpdated);
      this.$eventHub.$off('create-Dock', this.onDockCreated);
      this.$eventHub.$off('create-Appointment', this.onAppointmentCreated);
      this.$eventHub.$off('create-User', this.onUserCreated);
    },
    dismissBox() {
      this.$confirm('Are you sure you want to skip your Organization setup?').then(confirmed => {
        if (confirmed) {
          this.shouldShowWarehouseDialog = false;
          this.mixpanel.track(this.mixpanel.events.DISMISSED.GET_STARTED, {
            'Org ID': this.$org?.id,
            'Org Name': this.$org?.name
          });
          this.setIsOnboarded();
        }
      });
    },
    setStepNumber() {
      if (!this.hasOrg) {
        this.currentCoreStep = 1;
      } else if (!this.$appWarehouse?.id) {
        this.currentCoreStep = 2;
        this.$nextTick(() => {
          this.shouldShowWarehouseDialog = true;
        });
      } else if (!this.$hasSchedule) {
        this.currentCoreStep = 3;
      } else if (!this.$hasLoadType) {
        this.currentCoreStep = 4;
      } else if (!this.$hasDock) {
        this.currentCoreStep = 5;
      } else {
        this.currentCoreStep = 6;
      }

      if (this.currentCoreStep !== 2) {
        this.$nextTick(() => {
          this.shouldShowWarehouseDialog = false;
        });
      }

      this.$store.commit('App/setAreCoreStepsCompleted', this.areCoreStepsCompleted);
    },
    async setIsOnboarded() {
      await this.$store.dispatch('App/updateOrgIsOnboarded', {
        data: {
          isOnboarded: true
        },
        orgId: this.$me.orgId
      });
    },
    async handleInitialBoxDisplay() {
      this.destroyListeners();
      if (!this.novaCore.isInternalUser(this.$me)) {
        this.registerListeners();
      }
      if (!this.shouldBeOnboarded) {
        if (this.$org?.id) {
          if (!this.$org.isOnboarded) {
            await this.setIsOnboarded();
          } else {
            this.$store.commit('App/setIsOnboarded', true);
          }
        } else {
          this.$store.commit('App/setIsOnboarded', true);
        }
      } else {
        if (this.$org) {
          if (!this.$org.isOnboarded) {
            await this.getOneOrg();
            await this.getOneWarehouse();
            await this.getOneLoadType();
            await this.getOneEntity('dock', 'setHasDock');
            await this.getOneAppointment();
            await this.getOneOrgUser();
          } else {
            this.$store.commit('App/setIsOnboarded', this.$org.isOnboarded);
          }
        }
      }
      this.mounted = true;
    }
  },
  async destroyed() {
    this.destroyListeners();
  },
  watch: {
    hasOrg() {
      this.setStepNumber();
    },
    $hasLoadType() {
      this.setStepNumber();
    },
    $appWarehouse() {
      this.setStepNumber();
    },
    $hasSchedule() {
      this.setStepNumber();
    },
    $hasDock() {
      this.setStepNumber();
    },
    $hasAppointment() {
      if (this.$hasAppointment) {
        this.openAdditionalStepPanels = [];
      }
    },
    $hasInvitedUser() {
      if (this.$hasInvitedUser) {
        this.openAdditionalStepPanels = [];
      }
    },
    async $me() {
      if (!this.$me?.orgId || (this.$me.orgId && this.$org?.id && !this.mounted)) {
        await this.handleInitialBoxDisplay();
      }
    },
    async $org() {
      if (this.$me?.id && !this.mounted) {
        await this.handleInitialBoxDisplay();
      }
    },
    async areAdditionalStepsCompleted() {
      if (this.areAdditionalStepsCompleted && !this.$isOnboarded) {
        await this.setIsOnboarded();
      }
    }
  }
};
</script>
